import { useQuery } from '@tanstack/react-query';
import { type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { FieldList, MetaBox, MetaBoxLayout, PublishBox } from '@/components/admin/MetaBox';
import { ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';
import {
  MemberEventsTab,
  MemberHistoryTab,
  MemberMessagesTab,
  MemberScoresTab,
  MemberSuccessTab,
} from '@/features/members/MemberActivityTabs';
import { MemberPresidentSurveyTab } from '@/features/presidentSurveys/MemberPresidentSurveyTab';
import { MEMBER_STATUS_LABELS, fetchMember } from '@/features/members/api';
import { PhotoPreview } from '@/features/members/MemberPhotoField';
import { MemberRelatedNav } from '@/features/members/MemberRelatedNav';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

/**
 * 会員詳細。M2 の共通部品（メタボックス）を使い回す。
 * 活動情報（イベント・課題・スコアなど）は、タブ切替ではなく**見出し付きのセクションを
 * 縦に並べる**構成（D-149。ユーザー指摘「詳細ページはタブ区切りにする必要は無く、
 * セクションでブロック分けしたらよい」。タブは中身が見えず切り替えないと気づけないため）。
 * 編集操作は「プロフィール編集」（基本情報・写真・在籍状態の変更）と
 * 「連携設定編集」（Meet用メール・通知の受け取り方・ログイン情報）の2画面に分けた
 * （D-142・D-143）。事務局のみ編集ができる（画面側の出し分けであり、認可の本体は
 * API 側 / §0-6）。セクションもAPI側の認可（§6）に合わせてロールごとに出し分ける。
 * 参照できないセクションを表示して権限不足画面へ誘導するより、そもそも出さない方が
 * わかりやすい（M12後の指摘）。
 */
export function MemberDetailPage(): ReactElement {
  const { memberId = '' } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOffice = user?.active_role === 'office';
  const isDirector = user?.active_role === 'director';
  const isStudent = user?.active_role === 'student';
  const isSuccess = user?.active_role === 'success';

  const query = useQuery({
    queryKey: ['member', memberId],
    queryFn: () => fetchMember(memberId),
    enabled: Boolean(memberId),
  });

  if (query.isPending) return <SkeletonRows rows={6} />;
  if (query.isError) {
    const forbidden = query.error instanceof ApiError && query.error.status === 403;
    return forbidden ? (
      <ForbiddenState />
    ) : (
      <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
    );
  }

  const member = query.data;
  // 連絡先が null なのは「権限で隠されている」か「単に未入力」かの2通りがある。
  // API が項目を落とすのは PUBLIC_FIELDS スコープ（理事・講師と、D-105 で追加した
  // 受講生の他会員閲覧）のとき。それ以外のロールでは null=未入力として扱う（指示書 §6）。
  const contactHidden = user?.active_role === 'director' || user?.active_role === 'student';

  return (
    <>
      <PageHeader
        title={member.name}
        description={`会員ID: ${member.member_id}`}
      />
      <MemberRelatedNav memberId={member.id} current="detail" />

      <MetaBoxLayout
        main={
          <MetaBox title="基本情報">
            <FieldList
              fields={[
                {
                  label: '顔写真',
                  value: <PhotoPreview photoUrl={member.photo_url} name={member.name} size={56} />,
                },
                { label: '氏名', value: member.name },
                { label: 'ふりがな', value: member.name_kana ?? '—' },
                { label: '企業名', value: member.company ?? '—' },
                { label: '役職', value: member.position ?? '—' },
                {
                  label: '企業HP',
                  value: member.company_url ? (
                    <a
                      href={member.company_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-wpaccent hover:underline"
                    >
                      {member.company_url}
                    </a>
                  ) : (
                    '—'
                  ),
                },
                { label: '管掌領域', value: member.specialty ?? '—' },
                {
                  label: '部下人数',
                  value: member.subordinate_count !== null ? `${member.subordinate_count}名` : '—',
                },
                {
                  label: '勤続年数',
                  value: member.years_of_service !== null ? `${member.years_of_service}年` : '—',
                },
                { label: '期', value: member.cohort_name ?? '—' },
                { label: 'コース', value: member.course_name ?? '—' },
                { label: 'タグ', value: member.tags.length > 0 ? member.tags.join(', ') : '—' },
                // 先方のマイページ様式に合わせた公開プロフィール項目（D-148）
                { label: '趣味・特技・最近のマイブーム', value: member.hobbies ?? '—' },
                { label: '経歴', value: member.career_history ?? '—' },
                { label: 'CXO Growth参加目的', value: member.participation_purpose ?? '—' },
                { label: '強み', value: member.strengths ?? '—' },
                { label: '弱み', value: member.weaknesses ?? '—' },
                { label: '目標', value: member.goals ?? '—' },
                { label: '自己紹介', value: member.bio ?? '—' },
                {
                  label: '連絡先',
                  value: contactHidden ? (
                    <span className="text-wptext-muted">
                      このロールでは連絡先を表示できません（指示書 §6）
                    </span>
                  ) : (
                    [member.phone, member.contact_email].filter(Boolean).join(' / ') || '—'
                  ),
                },
                // ログインID（事務局のみ）。どのアドレスでログインするのか・
                // パスワード再設定メールがどこに届くのかが画面から分からなかった（D-178）
                ...(isOffice
                  ? [
                      {
                        label: 'ログインID（メールアドレス）',
                        value:
                          member.login_email ??
                          (member.user_id ? (
                            '—'
                          ) : (
                            <span className="text-wptext-muted">
                              ログインアカウントは未紐づけです（「連携設定編集」から招待できます）
                            </span>
                          )),
                      },
                    ]
                  : []),
              ]}
            />
          </MetaBox>
        }
        side={
          <PublishBox
            statusLabel={MEMBER_STATUS_LABELS[member.status]}
            updatedAt={member.updated_at ? formatDateTime(member.updated_at) : null}
            primaryLabel={isOffice ? 'プロフィールを編集' : undefined}
            onPrimary={isOffice ? () => navigate(`/members/${member.id}/edit`) : undefined}
            extra={
              isOffice ? (
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => navigate(`/members/${member.id}/integrations`)}
                    className="btn-secondary px-2 py-1 text-left"
                  >
                    連携設定を編集
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/members/${member.id}/events`)}
                    className="btn-secondary px-2 py-1 text-left"
                  >
                    参加イベント一覧
                  </button>
                </div>
              ) : undefined
            }
          />
        }
        below={
          // 事務局のみ。理事・講師／サクセス担当はプロフィール（公開項目）だけ（D-330）
          isOffice ? (
          <div className="flex flex-col gap-4">
            {/* 会員に固定の担当を付ける「担任制」はやめた（D-115）。
                誰がこの会員を見られるかは、イベントごとの担当者で決まる。
                イベント(出欠履歴): 理事は§6でNONE（担当会員でも出欠は見えない） */}
            {!isDirector && (
              <MetaBox title="イベント">
                <MemberEventsTab memberId={member.id} />
              </MetaBox>
            )}
            {/* 社長アンケート: サクセスも担当会員分を閲覧可（2026-08-18 先方回答 / D-104）。
                入力・修正は専用ページに寄せ、ここは閲覧だけにする（D-316） */}
            <MetaBox title="社長アンケート">
              <MemberPresidentSurveyTab memberId={member.id} readOnly />
              {isOffice && (
                <p className="mt-3 border-t border-wpborder pt-3">
                  <Link
                    to={`/members/${member.id}/president-survey`}
                    className="text-wpaccent hover:underline"
                  >
                    社長アンケートを入力・修正する
                  </Link>
                </p>
              )}
            </MetaBox>
            {/* メッセージ(全スレッド横断): 事務局のみ（D-027） */}
            {isOffice && (
              <MetaBox title="メッセージ">
                <MemberMessagesTab userId={member.user_id} />
              </MetaBox>
            )}
            {/* スコア内訳: 本人と事務局のみ（D-037）。理事・サクセスは対象外 */}
            {!isDirector && !isSuccess && (
              <MetaBox title="スコア">
                <MemberScoresTab memberId={member.id} />
              </MetaBox>
            )}
            {/* サクセス記録: 受講生本人・理事は対象外（§6。閲覧可はサクセス・事務局のみ） */}
            {!isStudent && !isDirector && (
              <MetaBox title="サクセス記録">
                <MemberSuccessTab memberId={member.id} />
              </MetaBox>
            )}
            {/* 履歴(監査ログ): 事務局のみ */}
            {isOffice && (
              <MetaBox title="履歴">
                <MemberHistoryTab memberId={member.id} />
              </MetaBox>
            )}
          </div>
          ) : undefined
        }
      />

      <button type="button" onClick={() => navigate(-1)} className="mt-4 text-wpaccent hover:underline">
        ← 一覧に戻る
      </button>
    </>
  );
}
